import React, { useContext, useState } from "react";
import { Modal, Button, Col, Row, ProgressBar } from "react-bootstrap";
import Icon from './Icon';
import { faSignOutAlt as logOutOutline, faCreditCard, faTrashAlt, faExclamationTriangle, faCheckCircle, faSun, faMoon, faCircleHalfStroke } from '@fortawesome/free-solid-svg-icons';
import { logout } from "./utils/logout";
import version from "../ver.json";
import { UserContext } from "./auth/auth";
import { useGravatar } from "../hooks/useGravatar";
import { useLicense } from "../hooks/useLicense";
import { useData, useGarbageCollect } from "../storage/accessors";
import { getDaysRemaining } from "@/key";
import { useNavigate } from "react-router";
import { useTheme } from "../contexts/themeContext";

interface ProfileModalProps {
    show: boolean;
    onHide: () => void;
}

export default function ProfileModal({ show, onHide }: ProfileModalProps) {
    const navigate = useNavigate();
    const user = React.useContext(UserContext);
    const { theme, setTheme } = useTheme();
    const email = user.email || "";
    const gravatarUrl = useGravatar(email, 200);

    // Получаем данные из DataContext
    const { tasks, checklistItems, trackers, taskLists, roles } = useData();

    // Получаем информацию о лицензии из useLicense
    const { licenseInfo } = useLicense();

    // Hook для garbage collection
    const { execute } = useGarbageCollect();

    // Состояние для модалки подтверждения удаления
    const [showGcModal, setShowGcModal] = React.useState(false);
    const [gcReport, setGcReport] = React.useState<ReturnType<typeof execute> | null>(null);

    const daysRemaining = licenseInfo?.planExp ? getDaysRemaining(licenseInfo.planExp) : 0;
    const limits = licenseInfo?.limits;

    // Вычисляем текущее использование
    const lifePartsUsed = Object.values(roles.objects).length;
    const trackersUsed = Object.values(trackers.objects).length;
    const listsUsed = Object.values(taskLists.objects).length;
    const tasksUsed = Object.values(tasks.objects).length;
    const checklistsUsed = Object.values(checklistItems.objects).length;
    const myWheelsUsed = user.wheels?.filter(w => w.owner === 'me').length || 0;
    const clientWheelsUsed = user.my_clients_wheels?.length || 0;

    // Хелпер: бар показывает оставшийся лимит, цвет зависит от % остатка
    const getLimitBarProps = (used: number, limit: number) => {
        const remaining = Math.max(0, limit - used);
        const pct = limit > 0 ? (remaining / limit) * 100 : 0;
        let variant: "success" | "warning" | "danger" = "success";
        if (pct < 25) variant = "danger";
        else if (pct < 50) variant = "warning";
        return { now: remaining, variant, remaining };
    };

    return (
        <Modal show={show} onHide={onHide}>
            <Modal.Header closeButton>
                <Modal.Title>Профиль</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row className="d-flex justify-content-center">
                    <Col xs={12}>
                        <div className="d-flex flex-column gap-3 align-items-center">
                            {email && (
                                <>
                                    <img
                                        src={gravatarUrl || '/logo.png'}
                                        alt="Gravatar"
                                        width={100}
                                        height={100}
                                        className="rounded-circle"
                                    />
                                    <div className="text-center">
                                        <strong>{email}</strong>
                                    </div>
                                </>
                            )}
                            <Button
                                variant="outline-danger"
                                size="lg"
                                onClick={() => {
                                    onHide();
                                    logout();
                                }}
                            >
                                <Icon icon={logOutOutline} className="me-2" />
                                Выйти из системы
                            </Button>
                        </div>
                        <div className="text-center mt-4 text-muted">
                            <small>Версия приложения: {version.ver}</small>
                        </div>

                        {/* Настройки темы */}
                        <div className="mt-4">
                            <hr />
                            <h5 className="mb-3">Тема оформления</h5>
                            <div className="d-flex gap-2 justify-content-center">
                                <Button
                                    variant={theme === 'light' ? 'primary' : 'outline-secondary'}
                                    size="sm"
                                    onClick={() => setTheme('light')}
                                >
                                    <Icon icon={faSun} className="me-1" />
                                    Светлая
                                </Button>
                                <Button
                                    variant={theme === 'dark' ? 'primary' : 'outline-secondary'}
                                    size="sm"
                                    onClick={() => setTheme('dark')}
                                >
                                    <Icon icon={faMoon} className="me-1" />
                                    Тёмная
                                </Button>
                                <Button
                                    variant={theme === 'auto' ? 'primary' : 'outline-secondary'}
                                    size="sm"
                                    onClick={() => setTheme('auto')}
                                >
                                    <Icon icon={faCircleHalfStroke} className="me-1" />
                                    Авто
                                </Button>
                            </div>
                        </div>

                        {/* Информация о тарифе */}
                        {licenseInfo && (
                            <div className="mt-4">
                                <hr />
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5 className="mb-0">Информация о тарифе</h5>

                                </div>

                                <div className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <strong>Тариф: {licenseInfo.plan}</strong>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => {
                                                onHide();
                                                navigate('/my/tariffs');
                                            }}
                                        >
                                            <Icon icon={faCreditCard} className="me-1" />
                                            Сменить тариф
                                        </Button>
                                    </div>
                                    {licenseInfo.plan.toLowerCase() !== 'free' && (
                                        <div className="text-center">
                                            <small className="text-muted">
                                                До истечения: <strong>{daysRemaining} дн.</strong>
                                            </small>
                                        </div>
                                    )}
                                </div>

                                {/* Прогресс-бары лимитов */}
                                {limits && (
                                    <div className="mt-3">
                                        {/* Лимит на части */}
                                        {limits.limit_life_part > 0 && (() => {
                                            const bar = getLimitBarProps(lifePartsUsed, limits.limit_life_part);
                                            return (
                                                <div className="mb-2">
                                                    <div className="d-flex justify-content-between mb-1">
                                                        <small>Части</small>
                                                        <small className="text-body-secondary">использовано {lifePartsUsed} / {limits.limit_life_part}</small>
                                                    </div>
                                                    <ProgressBar variant={bar.variant} now={bar.now} max={limits.limit_life_part} />
                                                </div>
                                            );
                                        })()}

                                        {/* Лимит на треки */}
                                        {limits.limit_trackers > 0 && (() => {
                                            const bar = getLimitBarProps(trackersUsed, limits.limit_trackers);
                                            return (
                                                <div className="mb-2">
                                                    <div className="d-flex justify-content-between mb-1">
                                                        <small>Трекеры</small>
                                                        <small className="text-body-secondary">использовано {trackersUsed} / {limits.limit_trackers}</small>
                                                    </div>
                                                    <ProgressBar variant={bar.variant} now={bar.now} max={limits.limit_trackers} />
                                                </div>
                                            );
                                        })()}

                                        {/* Лимит на списки */}
                                        {limits.limit_lists > 0 && (() => {
                                            const bar = getLimitBarProps(listsUsed, limits.limit_lists);
                                            return (
                                                <div className="mb-2">
                                                    <div className="d-flex justify-content-between mb-1">
                                                        <small>Списки</small>
                                                        <small className="text-body-secondary">использовано {listsUsed} / {limits.limit_lists}</small>
                                                    </div>
                                                    <ProgressBar variant={bar.variant} now={bar.now} max={limits.limit_lists} />
                                                </div>
                                            );
                                        })()}

                                        {/* Лимит на задачи */}
                                        {limits.limit_tasks > 0 && (() => {
                                            const bar = getLimitBarProps(tasksUsed, limits.limit_tasks);
                                            return (
                                                <div className="mb-2">
                                                    <div className="d-flex justify-content-between mb-1">
                                                        <small>Задачи</small>
                                                        <small className="text-body-secondary">использовано {tasksUsed} / {limits.limit_tasks}</small>
                                                    </div>
                                                    <ProgressBar variant={bar.variant} now={bar.now} max={limits.limit_tasks} />
                                                </div>
                                            );
                                        })()}

                                        {/* Лимит на чеклисты */}
                                        {limits.limit_checklists > 0 && (() => {
                                            const bar = getLimitBarProps(checklistsUsed, limits.limit_checklists);
                                            return (
                                                <div className="mb-2">
                                                    <div className="d-flex justify-content-between mb-1">
                                                        <small>Чеклисты</small>
                                                        <small className="text-body-secondary">использовано {checklistsUsed} / {limits.limit_checklists}</small>
                                                    </div>
                                                    <ProgressBar variant={bar.variant} now={bar.now} max={limits.limit_checklists} />
                                                </div>
                                            );
                                        })()}

                                        {/* Лимит на мои колеса */}
                                        {limits.limit_my_wheels > 0 && (() => {
                                            const bar = getLimitBarProps(myWheelsUsed, limits.limit_my_wheels);
                                            return (
                                                <div className="mb-2">
                                                    <div className="d-flex justify-content-between mb-1">
                                                        <small>Мои хранилища</small>
                                                        <small className="text-body-secondary">использовано {myWheelsUsed} / {limits.limit_my_wheels}</small>
                                                    </div>
                                                    <ProgressBar variant={bar.variant} now={bar.now} max={limits.limit_my_wheels} />
                                                </div>
                                            );
                                        })()}

                                        {/* Лимит на колеса клиентов */}
                                        {limits.limit_client_wheels > 0 && (() => {
                                            const bar = getLimitBarProps(clientWheelsUsed, limits.limit_client_wheels);
                                            return (
                                                <div className="mb-2">
                                                    <div className="d-flex justify-content-between mb-1">
                                                        <small>Хранилища клиентов</small>
                                                        <small className="text-body-secondary">использовано {clientWheelsUsed} / {limits.limit_client_wheels}</small>
                                                    </div>
                                                    <ProgressBar variant={bar.variant} now={bar.now} max={limits.limit_client_wheels} />
                                                </div>
                                            );
                                        })()}

                                        {/* Кнопка удаления ненужных данных */}
                                        <div className="mt-3 text-center">
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="text-muted"
                                                onClick={() => setShowGcModal(true)}
                                            >
                                                <Icon icon={faTrashAlt} className="me-1" />
                                                Удалить ненужные данные
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </Col>
                </Row>
            </Modal.Body>

            {/* Модалка подтверждения удаления данных */}
            <Modal show={showGcModal} onHide={() => { setShowGcModal(false); setGcReport(null); }} contentClassName="shadow-lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {gcReport ? (
                            <>
                                <Icon icon={faCheckCircle} className="me-2 text-success" />
                                Удаление завершено
                            </>
                        ) : (
                            <>
                                <Icon icon={faExclamationTriangle} className="me-2 text-warning" />
                                Подтверждение удаления
                            </>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {gcReport ? (
                        // Отчёт после удаления
                        <div>
                            <p className="mb-3">Удалено объектов:</p>
                            <ul className="list-unstyled">
                                {gcReport.trackersDeleted > 0 && (
                                    <li className="mb-1">
                                        <strong>Трекеры:</strong> {gcReport.trackersDeleted} шт.
                                    </li>
                                )}
                                {gcReport.tasksDeleted > 0 && (
                                    <li className="mb-1">
                                        <strong>Задачи:</strong> {gcReport.tasksDeleted} шт.
                                    </li>
                                )}
                                {gcReport.dayPlansDeleted > 0 && (
                                    <li className="mb-1">
                                        <strong>Планы на день:</strong> {gcReport.dayPlansDeleted} шт.
                                    </li>
                                )}
                                {gcReport.checklistItemsDeleted > 0 && (
                                    <li className="mb-1">
                                        <strong>Элементы чеклистов:</strong> {gcReport.checklistItemsDeleted} шт.
                                    </li>
                                )}
                                {gcReport.trackersDeleted === 0 &&
                                    gcReport.tasksDeleted === 0 &&
                                    gcReport.dayPlansDeleted === 0 &&
                                    gcReport.checklistItemsDeleted === 0 && (
                                        <li className="text-muted">Нет объектов для удаления</li>
                                    )}
                            </ul>
                        </div>
                    ) : (
                        // Информация перед удалением
                        <div>
                            <p>Будут удалены объекты следующих категорий:</p>
                            <ul className="list-unstyled">
                                <li className="mb-2">
                                    <strong>Трекеры:</strong>
                                    <br />
                                    <small className="text-muted">— которые указывают на несуществующие части</small>
                                </li>
                                <li className="mb-2">
                                    <strong>Задачи:</strong>
                                    <br />
                                    <small className="text-muted">— завершённые задачи с пустым контекстом</small>
                                </li>
                                <li className="mb-2">
                                    <strong>Планы на день:</strong>
                                    <br />
                                    <small className="text-muted">— завершённые старше 4 недель или ссылающиеся на несуществующие задачи</small>
                                </li>
                                <li className="mb-2">
                                    <strong>Элементы чеклистов:</strong>
                                    <br />
                                    <small className="text-muted">— которые указывают на несуществующие задачи</small>
                                </li>
                            </ul>
                            <div className="alert alert-warning mt-3">
                                <Icon icon={faExclamationTriangle} className="me-2" />
                                Это действие нельзя отменить!
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {gcReport ? (
                        <Button variant="secondary" onClick={() => { setShowGcModal(false); setGcReport(null); }}>
                            Закрыть
                        </Button>
                    ) : (
                        <>
                            <Button variant="secondary" onClick={() => { setShowGcModal(false); setGcReport(null); }}>
                                Отмена
                            </Button>
                            <Button
                                variant="danger"
                                onClick={() => {
                                    const report = execute();
                                    setGcReport(report);
                                }}
                            >
                                <Icon icon={faTrashAlt} className="me-2" />
                                Удалить
                            </Button>
                        </>
                    )}
                </Modal.Footer>
            </Modal>
        </Modal>
    );
}